/**
 * Port List Service — one API client per trade module's curated port list.
 *
 * Every trade module (Bunkers, Cargo, …) curates its own sub-list off the shared
 * UN/LOCODE master table. The endpoints are identical apart from the module
 * segment in the path, so this factory builds the client once and each module
 * gets an instance — that way the surfaces cannot drift apart.
 *
 * The backend forces the module's own flag on create and the detail endpoint
 * only ever resolves rows already on that list, so callers never send the flag.
 * ``removePort`` takes the port OFF this module's list; it does NOT delete the
 * master row (deleting a master UN/LOCODE row is superuser-only, in System Data).
 *
 * Mirrors the axios/auth pattern of systemDataService.
 */

import axiosInstance from '../utils/axiosConfig';

export const createPortListService = (moduleKey, label) => {
  const base = `/api/offer/${moduleKey}-ports/`;

  return {
    moduleKey,

    /**
     * Get a page of this module's ports (search + pagination, all server-side).
     * @param {Object} opts
     * @param {string} [opts.search] — substring match against port_name, unlocode, country_name
     * @param {number} [opts.limit=50] — page size
     * @param {number} [opts.offset=0] — number of rows to skip
     * @returns {Promise<{ports: Array, total: number}>} the page plus the full filtered count
     */
    getPorts: async ({ search = '', limit = 50, offset = 0 } = {}) => {
      try {
        const params = { limit, offset };
        if (search) params.search = search;
        const response = await axiosInstance.get(base, { params });
        const ports = response.data.ports || [];
        return { ports, total: response.data.total ?? ports.length };
      } catch (error) {
        console.error(`Error fetching ${label} ports:`, error);
        throw error;
      }
    },

    /**
     * Search the FULL master port table (member AND non-member rows) so the user
     * can flag an existing port instead of re-creating it. Same {ports, total}
     * shape as getPorts, but each port carries both module flags so the UI can
     * mark ports already on the list being managed.
     */
    searchMasterPorts: async ({ search = '', limit = 25, offset = 0 } = {}) => {
      try {
        const params = { limit, offset };
        if (search) params.search = search;
        const response = await axiosInstance.get(`${base}master-search/`, { params });
        const ports = response.data.ports || [];
        return { ports, total: response.data.total ?? ports.length };
      } catch (error) {
        console.error('Error searching master ports:', error);
        throw error;
      }
    },

    /**
     * Flag an existing master-list port for this module by id. Returns the
     * promoted port. 400s if the port is already on this list.
     */
    addExistingPort: async (portId) => {
      try {
        const response = await axiosInstance.post(`${base}add-existing/`, { port_id: portId });
        return response.data;
      } catch (error) {
        console.error(`Error adding existing ${label} port:`, error);
        throw error;
      }
    },

    /** Create a port. The module flag is forced True by the backend. */
    createPort: async (payload) => {
      try {
        const response = await axiosInstance.post(base, payload);
        return response.data;
      } catch (error) {
        console.error(`Error creating ${label} port:`, error);
        throw error;
      }
    },

    /**
     * Update a port. ``unlocode`` is immutable post-create — callers must omit
     * it from the patch payload.
     */
    updatePort: async (portId, patch) => {
      try {
        const response = await axiosInstance.patch(`${base}${portId}/`, patch);
        return response.data;
      } catch (error) {
        console.error(`Error updating ${label} port:`, error);
        throw error;
      }
    },

    /**
     * Remove a port from THIS module's list (clears the flag). The shared master
     * row survives — the other module and historical deals may still use it.
     */
    removePort: async (portId) => {
      try {
        const response = await axiosInstance.delete(`${base}${portId}/`);
        return response.data;
      } catch (error) {
        console.error(`Error removing ${label} port from the list:`, error);
        throw error;
      }
    },
  };
};

export default createPortListService;
